import React, { useContext, useMemo, useState } from 'react';
import { BinContext } from '../../../../../../../context/BinContext';
import { BinStatusContext } from '../../../../../../../context/BinStatusContext';
import { CustomFieldContext } from '../../../../../../../context/CustomFieldContext';
import {
  BinMutations,
  CreateBinResponse,
  CreateBinVariables,
  DeleteBinResponse,
  DeleteBinVariables,
  UpdateBinResponse,
  UpdateBinVariables,
} from '../../../../../../../graphql/bin.graphql';
import useMutation from '../../../../../../../hooks/useMutation';
import { useToast } from '../../../../../../../providers/ToastProvider';
import { BinInput } from '../../../../../../../types/bin';
import { Bin } from '../../../../../../../types/bin/bin';
import { CustomFieldEntityType } from '../../../../../../../types/custom_field/customField';
import { StockLocation } from '../../../../../../../types/stock_location/stockLocation';
import { TranslationKey } from '../../../../../../../types/translation/TranslationKey';
import { translate } from '../../../../../../../types/translation/Translator';
import { Button, ButtonVariant, CancelButton } from '../../../../../../components/Button';
import { DropdownSearchSelect } from '../../../../../../components/Dropdown';
import { ActionModal } from '../../../../../../components/Modal';
import { TextInput } from '../../../../../../components/TextInput';
import { ToastType } from '../../../../../../components/Toast';
import CustomFieldInput from '../../../../../Common/CustomFieldInput';
import { UserContext } from '../../../../../../../context/UserContext';
import { ContactContext } from '../../../../../../../context/ContactContext';
import { ProductMasterDataContext } from '../../../../../../../context/ProductMasterDataContext';
import { CompanyContext } from '../../../../../../../context/CompanyContext';

interface BinModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  stockLocation: StockLocation;
  bin: Bin | undefined;
}

export default function BinModal({ open, setOpen, bin, stockLocation }: BinModalProps) {
  const { showToast } = useToast();

  const { currentCompany } = useContext(CompanyContext);
  const { customFields } = useContext(CustomFieldContext);
  const { binStatuses } = useContext(BinStatusContext);
  const { bins, setBins } = useContext(BinContext);
  const { companyUsers } = useContext(UserContext);
  const { contacts } = useContext(ContactContext);
  const { productMasterData } = useContext(ProductMasterDataContext);

  const binCustomFields = useMemo(() => {
    return [...customFields.values()].filter(cf => cf.entityType === CustomFieldEntityType.bin);
  }, [customFields]);

  const [input, setInput] = useState(
    new BinInput(bin || { companyId: stockLocation.companyId, stockLocationId: stockLocation.id }),
  );

  const handleActionComplete = (bin: Bin) => {
    setBins(new Map(bins.set(bin.id, new Bin(bin))));
    setOpen(false);
  };

  const [create, { loading: createLoading }] = useMutation<CreateBinResponse, CreateBinVariables>(BinMutations.create, {
    variables: { bins: [input.forCreate()] },
    onCompleted: response => {
      const bin = response.createBin[0];
      handleActionComplete(bin);
      showToast({
        type: ToastType.success,
        title: translate({ key: TranslationKey.binCreatedSuccessfullyToastTitle }),
      });
    },
  });

  const [update, { loading: updateLoading }] = useMutation<UpdateBinResponse, UpdateBinVariables>(BinMutations.update, {
    variables: { bins: [input.forUpdate()] },
    onCompleted: response => {
      const bin = response.updateBin[0];
      handleActionComplete(bin);
      showToast({
        type: ToastType.success,
        title: translate({ key: TranslationKey.binUpdatedSuccessfullyToastTitle }),
      });
    },
  });

  const [remove, { loading: removeLoading }] = useMutation<DeleteBinResponse, DeleteBinVariables>(BinMutations.remove, {
    onCompleted: response => {
      bins.delete(response.deleteBin[0].id);
      setBins(new Map(bins));
      showToast({ type: ToastType.error, title: translate({ key: TranslationKey.binDeletedSuccessfullyToastTitle }) });
      setOpen(false);
    },
  });

  const handleSave = () => {
    if (bin) {
      update();
    } else {
      create();
    }
  };

  const valid = input.validate(customFields);
  const hasChanges = input.hasChanges(bin);

  const footer = (
    <div className='flex justify-between gap-2'>
      <div></div>

      <div className='flex gap-2'>
        <CancelButton onClick={() => setOpen(false)} />
        <Button
          variant={ButtonVariant.primary}
          text={translate({ key: bin ? TranslationKey.update : TranslationKey.create })}
          onClick={handleSave}
          loading={createLoading || updateLoading}
          disabled={!valid || !hasChanges || removeLoading}
        />
      </div>
    </div>
  );

  return (
    <ActionModal
      title={translate({ key: bin ? TranslationKey.updateBin : TranslationKey.createBin })}
      footer={footer}
      open={open}
      setOpen={setOpen}
    >
      <div className='grid gap-4'>
        <div className='grid gap-4'>
          <TextInput
            value={input.name}
            onChange={value => setInput(input.withName(value))}
            label={translate({ key: TranslationKey.name })}
            placeholder={translate({ key: TranslationKey.name })}
            mandatory
          />
          <DropdownSearchSelect
            mandatory
            label={translate({ key: TranslationKey.binStatus })}
            items={[...binStatuses.values()].filter(b => b.companyId === currentCompany.id)}
            onSelect={value => setInput(input.withBinStatusId(value?.id))}
            placeholder={translate({ key: TranslationKey.selectBinStatus })}
            textBuilder={item => item.status}
            value={binStatuses.get(input.binStatusId)}
          />
        </div>
        {binCustomFields.length ? (
          <div className='grid gap-2'>
            <p className='section-title text-base'>{translate({ key: TranslationKey.customFields })}</p>
            <div className='grid grid-cols-2 gap-4'>
              {binCustomFields.map(cf => (
                <div className='w-full'>
                  <CustomFieldInput
                    item={cf}
                    entity={input}
                    onChange={setInput}
                    value={input.customFields.get(cf.id)?.value}
                    users={companyUsers}
                    contacts={contacts}
                    productMasterData={productMasterData}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </ActionModal>
  );
}
